import {
  format,
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  isValid,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  isAfter,
  isBefore,
  isEqual,
  getMonth,
  getYear,
  getDate,
} from 'date-fns';
import { es } from 'date-fns/locale/es';

export const formatDate = (date, formatStr = 'dd/MM/yyyy') => {
  if (!date || !isValid(new Date(date))) return '';
  return format(new Date(date), formatStr, { locale: es });
};

export const formatTime = (date) => {
  if (!date || !isValid(new Date(date))) return '';
  return format(new Date(date), 'HH:mm', { locale: es });
};

export const formatFullDate = (date) => {
  if (!date || !isValid(new Date(date))) return '';
  return format(new Date(date), "dd 'de' MMMM 'de' yyyy", { locale: es });
};

export const formatMonthYear = (date) => {
  if (!date || !isValid(new Date(date))) return '';
  return format(new Date(date), 'MMMM yyyy', { locale: es });
};

export const formatMonth = (date) => {
  if (!date || !isValid(new Date(date))) return '';
  return format(new Date(date), 'MMMM', { locale: es });
};

export const formatMonthYearShort = (date) => {
  if (!date || !isValid(new Date(date))) return '';
  return format(new Date(date), 'MM-yyyy', { locale: es });
};

export const formatDateISO = (date) => {
  if (!date || !isValid(new Date(date))) return '';
  return format(new Date(date), 'yyyy-MM-dd', { locale: es });
};

export const formatDateSlash = (date) => {
  if (!date || !isValid(new Date(date))) return '';
  return format(new Date(date), 'dd/MM/yyyy', { locale: es });
};

export const addDaysToDate = (date, days) => {
  if (!date || !isValid(new Date(date))) return new Date();
  return addDays(new Date(date), days);
};

export const addMonthsToDate = (date, months) => {
  if (!date || !isValid(new Date(date))) return new Date();
  return addMonths(new Date(date), months);
};

export const addYearsToDate = (date, years) => {
  if (!date || !isValid(new Date(date))) return new Date();
  return addYears(new Date(date), years);
};

export const isDateAfter = (date1, date2) => {
  if (!date1 || !date2 || !isValid(new Date(date1)) || !isValid(new Date(date2))) return false;
  return isAfter(new Date(date1), new Date(date2));
};

export const isDateBefore = (date1, date2) => {
  if (!date1 || !date2 || !isValid(new Date(date1)) || !isValid(new Date(date2))) return false;
  return isBefore(new Date(date1), new Date(date2));
};

export const isDateEqual = (date1, date2) => {
  if (!date1 || !date2 || !isValid(new Date(date1)) || !isValid(new Date(date2))) return false;
  return isEqual(new Date(date1), new Date(date2));
};

export const getStartOfDay = (date) => {
  if (!date || !isValid(new Date(date))) return new Date();
  return startOfDay(new Date(date));
};

export const getEndOfDay = (date) => {
  if (!date || !isValid(new Date(date))) return new Date();
  return endOfDay(new Date(date));
};

export const getStartOfMonth = (date) => {
  if (!date || !isValid(new Date(date))) return new Date();
  return startOfMonth(new Date(date));
};

export const getEndOfMonth = (date) => {
  if (!date || !isValid(new Date(date))) return new Date();
  return endOfMonth(new Date(date));
};

export const getMonthFromDate = (date) => {
  if (!date || !isValid(new Date(date))) return 0;
  return getMonth(new Date(date));
};

export const getYearFromDate = (date) => {
  if (!date || !isValid(new Date(date))) return new Date().getFullYear();
  return getYear(new Date(date));
};

export const getDayFromDate = (date) => {
  if (!date || !isValid(new Date(date))) return 1;
  return getDate(new Date(date));
};

export const isValidDate = (date) => {
  if (!date) return false;
  try {
    const d = new Date(date);
    return isValid(d);
  } catch {
    return false;
  }
};

export const parseDate = (dateString) => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

export const utcToLocal = (utcDate) => {
  if (!utcDate || !isValid(new Date(utcDate))) return new Date();
  const date = new Date(utcDate);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};

export const localToUtc = (localDate) => {
  if (!localDate || !isValid(new Date(localDate))) return new Date();
  const date = new Date(localDate);
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

export const getDaysDifference = (date1, date2) => {
  if (!date1 || !date2 || !isValid(new Date(date1)) || !isValid(new Date(date2))) return 0;
  return differenceInDays(new Date(date1), new Date(date2));
};

export const convertMomentFormat = (momentFormat) => {
  return formatMapping[momentFormat] || momentFormat;
};

export const formatWithMomentFormat = (date, momentFormat) => {
  const dateFnsFormat = convertMomentFormat(momentFormat);
  return formatDate(date, dateFnsFormat);
};

export const getMinDate = () => {
  return addYearsToDate(new Date(), -1);
};

export const getMaxDate = () => {
  return addYearsToDate(new Date(), 2);
};

export const getDisabledDates = () => {
  const oneYearFromNow = addYearsToDate(new Date(), 1);
  const disabledDates = [];

  const startDisabled = new Date(oneYearFromNow);
  startDisabled.setDate(startDisabled.getDate() + 1);

  const endDisabled = addYearsToDate(new Date(), 2);

  for (let d = new Date(startDisabled); d <= endDisabled; d.setDate(d.getDate() + 1)) {
    disabledDates.push(new Date(d));
  }

  return disabledDates;
};

export const formatMapping = {
  LL: 'dd/MM/yyyy',
  dddd: 'EEEE',
  LT: 'HH:mm',
  'ddd D': 'EEE d',
  'MMM YYYY': 'MMM yyyy',
  'MM-YYYY': 'MM-yyyy',
  'YYYY-MM-DD': 'yyyy-MM-dd',
  'DD/MM/YYYY': 'dd/MM/yyyy',
  'DD MMM. HH:mm': 'dd MMM. HH:mm',
  'DD ddd HH:mm': 'dd EEE HH:mm',
  'D [de] MMMM [de] YYYY': "d 'de' MMMM 'de' yyyy",
};
